import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { IonicModule, ToastController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { UploadImageModule } from '../../../_shared/components/upload-image/upload-image.module';
import { VenueDetailsService } from '../venue-details.service';
import { Subject, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { VideoBoardSettings } from '../../../_shared/models/csTypes/videoSetting';
import { VenueVideoBoardsService } from '../venue-video-boards/venue-video-boards.service';
import { ToastService } from '../../../_core/services/toast.service';

// Constants
const LOGO_IDS = ['logo1', 'logo2'] as const;
const LOGO_NAMES = ['Logo 1', 'Logo 2'] as const;

// Type definitions
interface ImageUploadedData {
  image: string | null;
  imageChanged: boolean;
}

interface LogoData {
  id: string;
  name: string;
  image: string | null;
  imageChanged: boolean;
}

type LogoId = typeof LOGO_IDS[number];

interface ValidationError {
  field: string;
  message: string;
}

@Component({
  selector: 'app-video-board-settings',
  templateUrl: './video-board-settings.component.html',
  styleUrls: ['./video-board-settings.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule, UploadImageModule, FormsModule]
})
export class VideoBoardSettingsComponent implements OnInit, OnDestroy {
  private venueDetailsService = inject(VenueDetailsService);
  private toastController = inject(ToastService);
  private videoBoardSettingsService = inject(VenueVideoBoardsService);
  private readonly destroy$: Subject<void> = new Subject();

  // Reactive state management
  public isLoading = signal(false);
  public videoBoardSettingsLoaded = signal(false);
  public logos = signal<LogoData[]>([]);
  public selectedAddressFormat: string = 'none';
  public videoBoardSettings = signal<VideoBoardSettings | null>(null);
  public hasAddressFormatChanged = signal(false);
  addressFormats = [
    { code: 'none', name: 'None' },
    { code: 'city_state', name: 'City, State' },
    { code: 'country', name: 'Country' }
  ];

  constructor() { }

  ngOnInit() {
    this.initializeLogos();
    this.getVideoBoardSettings();
  }

  private initializeLogos(): void {
    const initialLogos: LogoData[] = LOGO_IDS.map((id, index) => ({
      id,
      name: LOGO_NAMES[index],
      image: null,
      imageChanged: false
    }));

    this.logos.set(initialLogos);
  }

  private async getVideoBoardSettings(): Promise<void> {
    await this.videoBoardSettingsService.videoBoards$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        videoBoards => {
          if (videoBoards) {
            this.videoBoardSettingsLoaded.set(true);
            const settings = videoBoards[0].venue?.settings || null;
            this.videoBoardSettings.set(settings);

            if (settings?.addressFormat) {
              this.selectedAddressFormat = settings.addressFormat;
            }
          }
        }
      )
  }

  public imageSelected(data: ImageUploadedData, logoId: LogoId): void {
    this.updateLogo(logoId, data);
  }

  private updateLogo(logoId: LogoId, data: ImageUploadedData): void {
    const currentLogos = this.logos();
    const logoIndex = currentLogos.findIndex(logo => logo.id === logoId);

    if (logoIndex === -1) {
      // console.error(`Logo with id ${logoId} not found`);
      return;
    }

    const updatedLogos = [...currentLogos];
    updatedLogos[logoIndex] = {
      ...updatedLogos[logoIndex],
      image: data.image,
      imageChanged: data.imageChanged
    };

    this.logos.set(updatedLogos);
  }

  public async submit(): Promise<void> {
    if (this.isLoading()) {
      return;
    }

    try {
      this.isLoading.set(true);

      // Get uploaded logos (button is already disabled if none exist)
      const uploadedLogos = this.logos().filter(logo => logo.image && logo.imageChanged);

      // Validate logo data
      const validationErrors = this.validateLogos(uploadedLogos);
      if (validationErrors.length > 0) {
        await this.showValidationErrors(validationErrors);
        return;
      }

      // Create and submit form data
      const formData = await this.createFormData(uploadedLogos, this.selectedAddressFormat);

      await this.submitLogos(formData);

    } catch (error) {
      // console.error('Error submitting logos:', error);
      await this.toastController.presentToast('Failed to upload logos. Please try again.', 'danger');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async createFormData(uploadedLogos: LogoData[], addressFormat: string): Promise<FormData> {
    const formData = new FormData();
    formData.append('addressFormat', addressFormat);
    for (const logo of uploadedLogos) {
      if (logo.image) {
        try {
          const blob = await fetch(logo.image).then(res => res.blob());
          formData.append(logo.id, blob);
        } catch (error) {
          // console.error(`Failed to convert image for ${logo.name}:`, error);
          throw new Error(`Failed to process ${logo.name}`);
        }
      }
    }

    formData.append('isImageChange', String(uploadedLogos.some(logo => logo.imageChanged)));

    return formData;
  }

  public addressFormatChanged(event: any): void {
    this.selectedAddressFormat = event;
    this.hasAddressFormatChanged.set(true);
  }

  private async submitLogos(formData: FormData): Promise<void> {
    return new Promise((resolve, reject) => {
      this.venueDetailsService
        .videoBoardUploadLogo(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (venue) => {
            this.clearUploadedLogos();
            this.toastController.presentToast('Settings updated successfully!');
            resolve();
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  private clearUploadedLogos(): void {
    const clearedLogos = this.logos().map(logo => ({
      ...logo,
      image: null,
      imageChanged: false
    }));
    this.logos.set(clearedLogos);
    this.hasAddressFormatChanged.set(false);
  }

  private validateLogos(logos: LogoData[]): ValidationError[] {
    const errors: ValidationError[] = [];

    logos.forEach(logo => {
      if (!logo.image) {
        errors.push({
          field: logo.id,
          message: `${logo.name} is required`
        });
      }
    });

    return errors;
  }

  private async showValidationErrors(errors: ValidationError[]): Promise<void> {
    const errorMessages = errors.map(error => error.message).join(', ');
    await this.toastController.presentToast(errorMessages, 'danger');
  }

  public getLogoImage(logoId: LogoId): string {
    const logo = this.logos().find(l => l.id === logoId);
    return logo?.image || '';
  }

  public getLogoById(logoId: LogoId): LogoData | undefined {
    return this.logos().find(logo => logo.id === logoId);
  }

  public hasUploadedLogos(): boolean {
    return this.logos().some(logo => logo.image && logo.imageChanged);
  }

  public isDisabled(): boolean {
    return this.hasUploadedLogos() || this.hasAddressFormatChanged();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
